import { Card, Table, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import type { SettlementRoot2, SettlementOffsetSummaryRoot } from '@repo/sdk'

import { formatKrw } from '@repo/shared'
import { formatKstDateTime } from '../../utils/formatKstDateTime'

const { Text } = Typography

interface SettlementOffsetsCardProps {
  settlement: SettlementRoot2
}

/**
 * Surfaces the offsets deducted from this settlement so the seller can see that
 * an amount is being clawed back and why. A standalone shipping-fee-only refund
 * on an already-paid-out order is recovered as such an offset against the
 * seller's next settlement; this card makes that deduction attributable to the
 * refund, exactly as it surfaces any other post-payout offset.
 */
export function SettlementOffsetsCard({ settlement }: SettlementOffsetsCardProps) {
  const { t } = useTranslation()

  const offsets = settlement.offsets ?? []
  if (offsets.length === 0) return null

  const columns = [
    {
      title: t('pages.settlements.offsets.columns.offsetAmount'),
      dataIndex: 'offsetAmount',
      key: 'offsetAmount',
      render: (_: unknown, record: SettlementOffsetSummaryRoot) => (
        <Text type="danger" data-testid="settlement-offset-amount">
          -{formatKrw(record.offsetAmount.amount)}
        </Text>
      ),
    },
    {
      title: t('pages.settlements.offsets.columns.reason'),
      dataIndex: 'reason',
      key: 'reason',
      render: (_: unknown, record: SettlementOffsetSummaryRoot) => (
        <Text data-testid="settlement-offset-reason">{record.reason}</Text>
      ),
    },
    {
      title: t('pages.settlements.offsets.columns.refundId'),
      dataIndex: 'refundId',
      key: 'refundId',
      render: (_: unknown, record: SettlementOffsetSummaryRoot) => (
        <Text copyable>{record.refundId}</Text>
      ),
    },
    {
      title: t('pages.settlements.offsets.columns.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_: unknown, record: SettlementOffsetSummaryRoot) =>
        formatKstDateTime(record.createdAt, 'YYYY-MM-DD HH:mm'),
    },
  ]

  return (
    <Card title={t('pages.settlements.sections.offsets')} bordered>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {t('pages.settlements.offsets.description')}
      </Text>
      <Table<SettlementOffsetSummaryRoot>
        data-testid="settlement-offsets-table"
        rowKey="offsetId"
        columns={columns}
        dataSource={offsets}
        pagination={false}
        size="small"
      />
    </Card>
  )
}
